import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Switch,
  Alert,
} from '@mui/material';
import { Author, CreateAuthorDto, UpdateAuthorDto } from '../types';

interface AuthorFormProps {
  open: boolean;
  title: string;
  author?: Author;
  onSave: (data: CreateAuthorDto | UpdateAuthorDto) => Promise<void>;
  onClose: () => void;
  loading?: boolean;
}

const AuthorForm: React.FC<AuthorFormProps> = ({
  open,
  title,
  author,
  onSave,
  onClose,
  loading = false,
}) => {
  const [formData, setFormData] = useState<CreateAuthorDto>({
    name: '',
    website: '',
    bio: '',
    avatar: '',
    isPublic: true,
  });
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (author) {
      setFormData({
        name: author.name,
        website: author.website || '',
        bio: author.bio || '',
        avatar: author.avatar || '',
        isPublic: author.isPublic,
      });
    } else {
      setFormData({
        name: '',
        website: '',
        bio: '',
        avatar: '',
        isPublic: true,
      });
    }
    setError(null);
  }, [author, open]);

  const handleChange = (field: keyof CreateAuthorDto) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: field === 'isPublic' ? (e.target as HTMLInputElement).checked : e.target.value,
    }));
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      setError('Author name is required');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      await onSave(formData);
      handleClose();
    } catch (err: any) {
      console.error('Error saving author:', err);
      setError(err.response?.data?.message || 'Failed to save author');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setError(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            fullWidth
            required
            label="Author Name"
            value={formData.name}
            onChange={handleChange('name')}
            placeholder="e.g., John Doe"
            disabled={submitting || loading}
          />

          <TextField
            fullWidth
            label="Website"
            value={formData.website}
            onChange={handleChange('website')}
            placeholder="https://..."
            disabled={submitting || loading}
          />

          <TextField
            fullWidth
            multiline
            rows={3}
            label="Bio"
            value={formData.bio}
            onChange={handleChange('bio')}
            placeholder="Brief description of the author"
            disabled={submitting || loading}
          />

          <TextField
            fullWidth
            label="Avatar URL"
            value={formData.avatar}
            onChange={handleChange('avatar')}
            placeholder="https://..."
            disabled={submitting || loading}
          />

          <FormControlLabel
            control={
              <Switch
                checked={formData.isPublic}
                onChange={handleChange('isPublic')}
                disabled={submitting || loading}
              />
            }
            label="Public (visible to all users)"
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={submitting || loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={submitting || loading || !formData.name.trim()}
        >
          {submitting ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AuthorForm;
